import type { ZudokuConfig, ZudokuPlugin } from "zudoku";

const SITE_URL = "https://docs.ejjy.io";
const OG_TITLE = "Ejjy Docs";
const OG_DESCRIPTION =
  "Guides, concepts, and the full API reference for deploying on Ejjy.";
const OG_IMAGE = `${SITE_URL}/opengraph.png`;

// OpenGraph + Twitter card meta tags. We return plain <meta> elements from
// a ZudokuPlugin.getHead() hook instead of using Helmet from the config.
// Zudoku's config is evaluated in a separate Vite environment whose
// react-helmet-async bundle has a different Context object than the one
// Bootstrap's HelmetProvider uses, so a <Head> in the config would
// dispatch into an empty context and crash SSR. PluginHeads wraps our
// returned JSX in Helmet using its own (matching) import.
const socialMetadataPlugin: ZudokuPlugin = {
  getHead: () => (
    <>
      <meta property="og:title" content={OG_TITLE} />
      <meta property="og:description" content={OG_DESCRIPTION} />
      <meta property="og:site_name" content="Ejjy" />
      <meta property="og:url" content={SITE_URL} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="600" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={OG_TITLE} />
      <meta name="twitter:description" content={OG_DESCRIPTION} />
      <meta name="twitter:image" content={OG_IMAGE} />
    </>
  ),
};

// Inline theme CSS. Kept here (instead of a side-car file) because both
// Zudoku's dev config loader and its production build expect a plain
// string; shuttling the content through fs.readFileSync or a `?raw` import
// breaks in one environment or the other.
const customCss = `
:root {
  --ejjy-orange: #F5761A;
  --ejjy-ink:    #0A0A0B;
  --ejjy-paper:  #ECECEE;
  --ejjy-panel:  #141416;
  --ejjy-line:   #23232A;

  --primary: var(--ejjy-orange);
  --primary-foreground: #ffffff;
  --ring: var(--ejjy-orange);
  --sidebar-primary: var(--ejjy-orange);
  --sidebar-primary-foreground: #ffffff;
}

.dark {
  --background: var(--ejjy-ink);
  --foreground: var(--ejjy-paper);
  --card: var(--ejjy-panel);
  --card-foreground: var(--ejjy-paper);
  --popover: var(--ejjy-panel);
  --popover-foreground: var(--ejjy-paper);
  --sidebar: var(--ejjy-ink);
  --sidebar-foreground: var(--ejjy-paper);
  --sidebar-border: var(--ejjy-line);
  --sidebar-accent: var(--ejjy-panel);
  --sidebar-accent-foreground: var(--ejjy-paper);
  --border: var(--ejjy-line);
  --input: var(--ejjy-line);
  --primary: var(--ejjy-orange);
  --primary-foreground: #ffffff;
  --sidebar-primary: var(--ejjy-orange);
  --sidebar-primary-foreground: #ffffff;
  --ring: var(--ejjy-orange);
}

body {
  font-family: var(--font-sans);
}

h1, h2, h3, h4, h5, h6 {
  font-family: var(--font-serif);
}

/* Hide the theme toggle. Re-enable by deleting this rule; both light
   and dark token sets remain wired up. aria-label covers all three
   states next-themes cycles through. */
button[aria-label="Toggle theme"],
button[aria-label="Switch to light mode"],
button[aria-label="Switch to dark mode"] {
  display: none;
}
`;

const config: ZudokuConfig = {
  site: {
    showPoweredBy: false,
    logo: {
      src: {
        light: "/brand/lockup/ejjy-lockup-color-on-light.svg",
        dark: "/brand/lockup/ejjy-lockup-color-on-dark.svg",
      },
      alt: "Ejjy",
      width: "130px",
    },
  },
  metadata: {
    title: OG_TITLE,
    description: OG_DESCRIPTION,
    favicon: "/brand/favicon/favicon.svg",
  },
  plugins: [socialMetadataPlugin],
  theme: {
    fonts: {
      sans: "DM Sans",
      serif: "Space Grotesk",
    },
    customCss,
  },
  navigation: [
    {
      type: "category",
      label: "Documentation",
      // Use the object form so we can override the resolved route to
      // `/`. The shorthand "/index" would register at `/index`, and the
      // client router would 404 on a direct visit to `/` because the
      // prerendered file is served but no route matches after hydration.
      items: [{ type: "doc", file: "/index", path: "/" }],
    },
    {
      type: "link",
      to: "/api",
      label: "API Reference",
    },
  ],
  apis: [
    {
      type: "file",
      // Regenerate the committed spec with:
      //   just docs-spec
      input: "./apis/openapi.json",
      path: "/api",
    },
  ],
};

export default config;
